import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Users, Plus, UserCheck, UserX, X, Mail, Phone, CreditCard, DollarSign, MapPin, FileText, Edit, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import './Funcionarios.css';

const Funcionarios = () => {
  const [filtroCargo, setFiltroCargo] = useState('todos');
  const [modalAberto, setModalAberto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [confirmarDeletar, setConfirmarDeletar] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    telefone: '',
    cpf: '',
    cargo: 'caixa',
    salario: '',
    comissao: '',
    endereco: {
      rua: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: ''
    },
    observacoes: ''
  });
  const [erros, setErros] = useState({});
  const queryClient = useQueryClient();

  // Buscar estabelecimento
  const { data: estabelecimentoData } = useQuery(
    'estabelecimento-funcionarios',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        return null;
      }
    }
  );

  const estabelecimentoId = estabelecimentoData?._id;

  const { data: funcionarios } = useQuery(
    ['funcionarios', filtroCargo, estabelecimentoId],
    async () => {
      if (!estabelecimentoId) return { funcionarios: [] };
      const params = new URLSearchParams();
      params.append('estabelecimento', estabelecimentoId);
      if (filtroCargo !== 'todos') {
        params.append('cargo', filtroCargo);
      }
      const response = await api.get(`/api/funcionarios?${params}`);
      return response.data.data;
    },
    {
      enabled: !!estabelecimentoId
    }
  );

  const cargos = ['gerente', 'caixa', 'garcom', 'cozinheiro', 'motoboy', 'auxiliar', 'outro'];

  // Mutation para criar funcionário
  const criarFuncionarioMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/funcionarios', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Funcionário cadastrado com sucesso!');
        queryClient.invalidateQueries(['funcionarios']);
        setModalAberto(false);
        resetForm();
      },
      onError: (error) => {
        const mensagem = error.response?.data?.message || 'Erro ao cadastrar funcionário';
        toast.error(mensagem);
        if (error.response?.data?.errors) {
          setErros(error.response.data.errors);
        }
      }
    }
  );

  // Mutation para atualizar funcionário
  const atualizarFuncionarioMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/api/funcionarios/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Funcionário atualizado com sucesso!');
        queryClient.invalidateQueries(['funcionarios']);
        setModalAberto(false);
        resetForm();
      },
      onError: (error) => {
        const mensagem = error.response?.data?.message || 'Erro ao atualizar funcionário';
        toast.error(mensagem);
        if (error.response?.data?.errors) {
          setErros(error.response.data.errors);
        }
      }
    }
  );

  // Mutation para deletar funcionário
  const deletarFuncionarioMutation = useMutation(
    async ({ id, deletarUsuario }) => {
      const params = deletarUsuario ? '?deletarUsuario=true' : '';
      const response = await api.delete(`/api/funcionarios/${id}${params}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Funcionário deletado com sucesso!');
        queryClient.invalidateQueries(['funcionarios']);
        setConfirmarDeletar(null);
      },
      onError: (error) => {
        const mensagem = error.response?.data?.message || 'Erro ao deletar funcionário';
        toast.error(mensagem);
      }
    }
  );

  // Buscar funcionário para edição
  const { data: funcionarioEdicao } = useQuery(
    ['funcionario', editandoId],
    async () => {
      if (!editandoId) return null;
      const response = await api.get(`/api/funcionarios/${editandoId}`);
      return response.data.data.funcionario;
    },
    {
      enabled: !!editandoId
    }
  );

  // Carregar dados do funcionário quando abrir modal de edição
  useEffect(() => {
    if (funcionarioEdicao && editandoId) {
      setFormData({
        nome: funcionarioEdicao.usuario?.nome || '',
        email: funcionarioEdicao.usuario?.email || '',
        senha: '', // Não carregar senha
        telefone: funcionarioEdicao.telefone || funcionarioEdicao.usuario?.telefone || '',
        cpf: funcionarioEdicao.cpf || '',
        cargo: funcionarioEdicao.cargo || 'caixa',
        salario: funcionarioEdicao.salario || '',
        comissao: funcionarioEdicao.comissao || '',
        endereco: funcionarioEdicao.endereco || {
          rua: '',
          numero: '',
          complemento: '',
          bairro: '',
          cidade: '',
          estado: '',
          cep: ''
        },
        observacoes: funcionarioEdicao.observacoes || ''
      });
      setModalAberto(true);
    }
  }, [funcionarioEdicao, editandoId]);

  const resetForm = () => {
    setFormData({
      nome: '',
      email: '',
      senha: '',
      telefone: '',
      cpf: '',
      cargo: 'caixa',
      salario: '',
      comissao: '',
      endereco: {
        rua: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        estado: '',
        cep: ''
      },
      observacoes: ''
    });
    setErros({});
    setEditandoId(null);
  };

  const abrirModalEdicao = (funcionarioId) => {
    setEditandoId(funcionarioId);
  };

  const fecharModal = () => {
    if (!criarFuncionarioMutation.isLoading && !atualizarFuncionarioMutation.isLoading) {
      setModalAberto(false);
      resetForm();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('endereco.')) {
      const campo = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          [campo]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Limpar erro do campo quando começar a digitar
    if (erros[name]) {
      setErros(prev => {
        const novo = { ...prev };
        delete novo[name];
        return novo;
      });
    }
  };

  const validarForm = () => {
    const novosErros = {};
    
    if (!formData.nome.trim()) novosErros.nome = 'Nome é obrigatório';
    if (!formData.email.trim()) novosErros.email = 'Email é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) novosErros.email = 'Email inválido';
    // Senha só é obrigatória ao criar, não ao editar
    if (!editandoId && !formData.senha) novosErros.senha = 'Senha é obrigatória';
    else if (formData.senha && formData.senha.length < 6) novosErros.senha = 'Senha deve ter no mínimo 6 caracteres';
    if (!formData.cargo) novosErros.cargo = 'Cargo é obrigatório';
    if (formData.cpf && !/^\d{11}$/.test(formData.cpf.replace(/\D/g, ''))) {
      novosErros.cpf = 'CPF deve conter 11 dígitos';
    }
    if (formData.salario && isNaN(parseFloat(formData.salario))) {
      novosErros.salario = 'Salário deve ser um número válido';
    }
    if (formData.comissao && (isNaN(parseFloat(formData.comissao)) || parseFloat(formData.comissao) < 0 || parseFloat(formData.comissao) > 100)) {
      novosErros.comissao = 'Comissão deve ser um número entre 0 e 100';
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validarForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    const dadosEnvio = {
      nome: formData.nome.trim(),
      email: formData.email.trim().toLowerCase(),
      telefone: formData.telefone.replace(/\D/g, '') || undefined,
      cpf: formData.cpf.replace(/\D/g, '') || undefined,
      cargo: formData.cargo,
      salario: formData.salario ? parseFloat(formData.salario) : undefined,
      comissao: formData.comissao ? parseFloat(formData.comissao) : 0,
      observacoes: formData.observacoes.trim() || undefined
    };

    // Adicionar senha apenas se fornecida (ou se for criação)
    if (formData.senha) {
      dadosEnvio.senha = formData.senha;
    }

    // Adicionar endereço apenas se tiver pelo menos um campo preenchido
    const temEndereco = Object.values(formData.endereco).some(v => v.trim());
    if (temEndereco) {
      dadosEnvio.endereco = {};
      if (formData.endereco.rua) dadosEnvio.endereco.rua = formData.endereco.rua.trim();
      if (formData.endereco.numero) dadosEnvio.endereco.numero = formData.endereco.numero.trim();
      if (formData.endereco.complemento) dadosEnvio.endereco.complemento = formData.endereco.complemento.trim();
      if (formData.endereco.bairro) dadosEnvio.endereco.bairro = formData.endereco.bairro.trim();
      if (formData.endereco.cidade) dadosEnvio.endereco.cidade = formData.endereco.cidade.trim();
      if (formData.endereco.estado) dadosEnvio.endereco.estado = formData.endereco.estado.trim();
      if (formData.endereco.cep) dadosEnvio.endereco.cep = formData.endereco.cep.replace(/\D/g, '').trim();
    }

    if (editandoId) {
      atualizarFuncionarioMutation.mutate({ id: editandoId, data: dadosEnvio });
    } else {
      dadosEnvio.estabelecimento = estabelecimentoId;
      criarFuncionarioMutation.mutate(dadosEnvio);
    }
  };

  return (
    <div className="funcionarios-page">
      <div className="page-header">
        <div>
          <h2><Users size={28} /> Funcionários</h2>
          <p>Gerencie sua equipe</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setModalAberto(true)}
        >
          <Plus size={20} />
          Novo Funcionário
        </button>
      </div>

      <div className="filtros-funcionarios">
        <select
          value={filtroCargo}
          onChange={(e) => setFiltroCargo(e.target.value)}
          className="select-cargo"
        >
          <option value="todos">Todos os cargos</option>
          {cargos.map(cargo => (
            <option key={cargo} value={cargo}>
              {cargo.charAt(0).toUpperCase() + cargo.slice(1)}
            </option>
          ))}
        </select>
      </div>

      {!estabelecimentoId && (
        <div className="empty-state">
          <Users size={64} />
          <p>Nenhum estabelecimento encontrado. Configure seu estabelecimento primeiro.</p>
        </div>
      )}

      {estabelecimentoId && (
        <>
          {funcionarios?.funcionarios?.length > 0 ? (
            <div className="funcionarios-grid">
              {funcionarios.funcionarios.map(funcionario => (
                <div key={funcionario._id} className={`funcionario-card ${funcionario.ativo ? 'ativo' : 'inativo'}`}>
                  <div className="funcionario-header">
                    <div>
                      <h3>{funcionario.usuario?.nome}</h3>
                      <p className="cargo-badge">{funcionario.cargo}</p>
                    </div>
                    {funcionario.ativo ? (
                      <UserCheck size={24} className="status-icon ativo" />
                    ) : (
                      <UserX size={24} className="status-icon inativo" />
                    )}
                  </div>
                  <div className="funcionario-info">
                    <p><strong>Email:</strong> {funcionario.usuario?.email}</p>
                    <p><strong>Telefone:</strong> {funcionario.telefone || 'N/A'}</p>
                    <p><strong>CPF:</strong> {funcionario.cpf || 'N/A'}</p>
                    {funcionario.salario && (
                      <p><strong>Salário:</strong> R$ {funcionario.salario.toFixed(2)}</p>
                    )}
                    {funcionario.comissao > 0 && (
                      <p><strong>Comissão:</strong> {funcionario.comissao}%</p>
                    )}
                  </div>
                  <div className="funcionario-footer">
                    <span className={`status-badge ${funcionario.ativo ? 'ativo' : 'inativo'}`}>
                      {funcionario.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                    <div className="funcionario-actions">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => abrirModalEdicao(funcionario._id)}
                        title="Editar funcionário"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => setConfirmarDeletar(funcionario)}
                        title="Deletar funcionário"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Users size={64} />
              <p>Nenhum funcionário cadastrado ainda</p>
              <button 
                className="btn btn-primary"
                onClick={() => setModalAberto(true)}
                style={{ marginTop: '1rem' }}
              >
                <Plus size={20} />
                Cadastrar Primeiro Funcionário
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal de Cadastro */}
      {modalAberto && (
        <div className="modal-overlay" onClick={() => !criarFuncionarioMutation.isLoading && setModalAberto(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editandoId ? (
                  <>
                    <Edit size={24} />
                    Editar Funcionário
                  </>
                ) : (
                  <>
                    <Plus size={24} />
                    Novo Funcionário
                  </>
                )}
              </h2>
              <button 
                className="btn-close-modal"
                onClick={fecharModal}
                disabled={criarFuncionarioMutation.isLoading || atualizarFuncionarioMutation.isLoading}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="form-funcionario">
              <div className="form-section">
                <h3>Dados Pessoais</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className={erros.nome ? 'input-error' : ''}
                      required
                    />
                    {erros.nome && <span className="error-message">{erros.nome}</span>}
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <div className="input-with-icon">
                      <Mail size={18} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={erros.email ? 'input-error' : ''}
                        required
                      />
                    </div>
                    {erros.email && <span className="error-message">{erros.email}</span>}
                  </div>

                  <div className="form-group">
                    <label>Senha {!editandoId && '*'}</label>
                    <input
                      type="password"
                      name="senha"
                      value={formData.senha}
                      onChange={handleChange}
                      className={erros.senha ? 'input-error' : ''}
                      minLength={6}
                      required={!editandoId}
                      placeholder={editandoId ? 'Deixe em branco para manter a senha atual' : ''}
                    />
                    {editandoId && (
                      <p className="info-text">Deixe em branco para manter a senha atual</p>
                    )}
                    {erros.senha && <span className="error-message">{erros.senha}</span>}
                  </div>

                  <div className="form-group">
                    <label>Telefone</label>
                    <div className="input-with-icon">
                      <Phone size={18} />
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>CPF</label>
                    <div className="input-with-icon">
                      <CreditCard size={18} />
                      <input
                        type="text"
                        name="cpf"
                        value={formData.cpf}
                        onChange={handleChange}
                        className={erros.cpf ? 'input-error' : ''}
                        placeholder="000.000.000-00"
                        maxLength={14}
                      />
                    </div>
                    {erros.cpf && <span className="error-message">{erros.cpf}</span>}
                  </div>

                  <div className="form-group">
                    <label>Cargo *</label>
                    <select
                      name="cargo"
                      value={formData.cargo}
                      onChange={handleChange}
                      className={erros.cargo ? 'input-error' : ''}
                      required
                    >
                      {cargos.map(cargo => (
                        <option key={cargo} value={cargo}>
                          {cargo.charAt(0).toUpperCase() + cargo.slice(1)}
                        </option>
                      ))}
                    </select>
                    {formData.cargo === 'caixa' && (
                      <p className="info-text">⚠️ Funcionários com cargo "Caixa" terão acesso ao PDV</p>
                    )}
                    {formData.cargo === 'motoboy' && (
                      <p className="info-text">⚠️ Funcionários com cargo "Motoboy" terão acesso ao Dashboard de Entregador</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Dados Financeiros</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Salário</label>
                    <div className="input-with-icon">
                      <DollarSign size={18} />
                      <input
                        type="number"
                        name="salario"
                        value={formData.salario}
                        onChange={handleChange}
                        className={erros.salario ? 'input-error' : ''}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    {erros.salario && <span className="error-message">{erros.salario}</span>}
                  </div>

                  <div className="form-group">
                    <label>Comissão (%)</label>
                    <input
                      type="number"
                      name="comissao"
                      value={formData.comissao}
                      onChange={handleChange}
                      className={erros.comissao ? 'input-error' : ''}
                      placeholder="0"
                      step="0.1"
                      min="0"
                      max="100"
                    />
                    {erros.comissao && <span className="error-message">{erros.comissao}</span>}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Endereço (Opcional)</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Rua</label>
                    <div className="input-with-icon">
                      <MapPin size={18} />
                      <input
                        type="text"
                        name="endereco.rua"
                        value={formData.endereco.rua}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Número</label>
                    <input
                      type="text"
                      name="endereco.numero"
                      value={formData.endereco.numero}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>CEP</label>
                    <input
                      type="text"
                      name="endereco.cep"
                      value={formData.endereco.cep}
                      onChange={handleChange}
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </div>

                  <div className="form-group">
                    <label>Complemento</label>
                    <input
                      type="text"
                      name="endereco.complemento"
                      value={formData.endereco.complemento}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Bairro</label>
                    <input
                      type="text"
                      name="endereco.bairro"
                      value={formData.endereco.bairro}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Cidade</label>
                    <input
                      type="text"
                      name="endereco.cidade"
                      value={formData.endereco.cidade}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Estado</label>
                    <input
                      type="text"
                      name="endereco.estado"
                      value={formData.endereco.estado}
                      onChange={handleChange}
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div className="form-group full-width">
                  <label>Observações</label>
                  <div className="input-with-icon">
                    <FileText size={18} />
                    <textarea
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Observações adicionais sobre o funcionário..."
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={fecharModal}
                  disabled={criarFuncionarioMutation.isLoading || atualizarFuncionarioMutation.isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={criarFuncionarioMutation.isLoading || atualizarFuncionarioMutation.isLoading}
                >
                  {editandoId 
                    ? (atualizarFuncionarioMutation.isLoading ? 'Salvando...' : 'Salvar Alterações')
                    : (criarFuncionarioMutation.isLoading ? 'Cadastrando...' : 'Cadastrar Funcionário')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Deletar */}
      {confirmarDeletar && (
        <div className="modal-overlay" onClick={() => !deletarFuncionarioMutation.isLoading && setConfirmarDeletar(null)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Trash2 size={24} />
                Confirmar Exclusão
              </h2>
              <button 
                className="btn-close-modal"
                onClick={() => !deletarFuncionarioMutation.isLoading && setConfirmarDeletar(null)}
                disabled={deletarFuncionarioMutation.isLoading}
              >
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p>Tem certeza que deseja deletar o funcionário <strong>{confirmarDeletar.usuario?.nome}</strong>?</p>
              <div className="confirm-options">
                <label>
                  <input
                    type="checkbox"
                    id="deletarUsuario"
                    onChange={(e) => {
                      // Armazenar opção no estado
                      setConfirmarDeletar(prev => ({ ...prev, deletarUsuario: e.target.checked }));
                    }}
                  />
                  <span>Também deletar o usuário associado (não poderá mais fazer login)</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmarDeletar(null)}
                disabled={deletarFuncionarioMutation.isLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  deletarFuncionarioMutation.mutate({
                    id: confirmarDeletar._id,
                    deletarUsuario: confirmarDeletar.deletarUsuario || false
                  });
                }}
                disabled={deletarFuncionarioMutation.isLoading}
              >
                {deletarFuncionarioMutation.isLoading ? 'Deletando...' : 'Deletar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Funcionarios;

